import { DateTime } from "luxon";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/**
 * Get localized week day names (short format), always Sunday-first.
 * luxonConfig sets a global `Settings.defaultWeekSettings.firstDay = 7` (Sunday), so
 * `startOf("week")` lands on Sunday for EVERY locale — the array is Sunday-first regardless
 * of the locale's natural week start. Only the label TEXT is localized, not the order.
 * @returns Array of 7 Sunday-first day names localized to the current language
 * - English: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
 * - Spanish: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"]
 */
export const useLocalizedWeekDays = (format: "ccc" | "cccc" | "ccccc" = "ccc"): string[] => {
	const { i18n } = useTranslation();

	return useMemo(() => {
		// Start from the locale's week start day (Luxon global locale is already set by i18n)
		const startOfWeek = DateTime.now().startOf("week");
		const dayNames = Array.from({ length: 7 }, (_, i) => startOfWeek.plus({ day: i }).toFormat(format));

		if (i18n.language === "es") {
			return dayNames.map((day) => {
				const withoutPeriod = day.replace(/\.$/, "");
				return withoutPeriod.charAt(0).toUpperCase() + withoutPeriod.slice(1);
			});
		}

		return dayNames;
	}, [format, i18n.language]);
};
